import clsx from 'clsx';
import type { ClientApproval, VacationRequest } from '../../../models/vacation.types';
import { resolveClientResendAvailability } from '../../../utils/vacationClientResend';
import {
    CLIENT_STATUS_CONFIRMED,
    CLIENT_STATUS_NOT_NEEDED,
    CLIENT_STATUS_PENDING,
    CLIENT_STATUS_REJECTED,
    CLIENTS_TITLE,
    RESEND_COOLDOWN_MESSAGE,
} from '../vacationRequestDetailsModal.definitions';
import { ResendClientEmailAction } from './ResendClientEmailAction';

export interface RequestClientsProps {
    clientEmails: string[] | null | undefined;
    clientApproval: ClientApproval;
    request?: VacationRequest | null;
    onResendClientEmail?: (requestId: string, email: string) => void;
    resendingEmail?: string | null;
}

interface ClientRow {
    email: string;
    label: string;
    confirmed: boolean;
}

/**
 * clientApproval é UM campo só para N e-mails — "1 de N basta" confirma em nome de todos. Não dá
 * para saber, sem consultar vacationClientTokens (default-deny, só o Admin SDK lê), quem exatamente
 * recebeu o link e ainda não abriu. O que dá para mostrar com honestidade: quem confirmou (by) e,
 * uma vez confirmado, que os demais não precisaram.
 */
function buildClientRows(emails: string[], approval: ClientApproval): ClientRow[] {
    return emails.map((email) => {
        const isConfirmedByThisEmail =
            approval.status === 'Approved' &&
            approval.by?.toLowerCase() === email.toLowerCase();

        return {
            email,
            confirmed: isConfirmedByThisEmail,
            label:
                approval.status === 'Approved'
                    ? isConfirmedByThisEmail
                        ? CLIENT_STATUS_CONFIRMED
                        : CLIENT_STATUS_NOT_NEEDED
                    : approval.status === 'Rejected'
                        ? CLIENT_STATUS_REJECTED
                        : CLIENT_STATUS_PENDING,
        };
    });
}

export function RequestClients({
    clientEmails,
    clientApproval,
    request,
    onResendClientEmail,
    resendingEmail,
}: RequestClientsProps) {
    if (clientApproval.status === 'None' || !clientEmails || clientEmails.length === 0) {
        return null;
    }

    const rows = buildClientRows(clientEmails, clientApproval);

    return (
        <div>
            <h3 className="mb-3 text-sm font-semibold text-raro-gray-900 font-montserrat">
                {CLIENTS_TITLE}
            </h3>
            <ul className="space-y-2">
                {rows.map((row) => {
                    const { showAction, canResend } = request
                        ? resolveClientResendAvailability({
                              email: row.email,
                              request,
                          })
                        : { showAction: false, canResend: false };

                    const isResending = resendingEmail?.toLowerCase() === row.email.toLowerCase();

                    return (
                        <div key={row.email}>
                            <li className="flex items-center justify-between gap-3 rounded-xl bg-raro-surface-50 px-4 py-2.5">
                                <span className="break-all text-sm font-medium text-raro-gray-800 font-montserrat">
                                    {row.email}
                                </span>
                                <div className="flex shrink-0 items-center gap-3">
                                    {showAction && onResendClientEmail && (
                                        <ResendClientEmailAction
                                            email={row.email}
                                            canResend={canResend}
                                            isResending={isResending}
                                            onConfirm={() =>
                                                onResendClientEmail(request!.id, row.email)
                                            }
                                        />
                                    )}
                                    <span
                                        className={clsx(
                                            'shrink-0 text-xs font-semibold font-montserrat',
                                            row.confirmed
                                                ? 'text-emerald-700'
                                                : 'text-raro-gray-500'
                                        )}
                                    >
                                        {row.label}
                                    </span>
                                </div>
                            </li>
                            {showAction && !canResend && (
                                <p className="mt-1 text-xs text-raro-gray-500 font-montserrat px-4">
                                    {RESEND_COOLDOWN_MESSAGE}
                                </p>
                            )}
                        </div>
                    );
                })}
            </ul>
        </div>
    );
}

export default RequestClients;
